"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  Keyboard,
  Lampboard,
  PressLog,
  RotorWindows,
  SignalRoute,
  slotNames,
} from "@/components/tools/cipher-machines/enigma-panels";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  ALPHABET,
  groupLetters,
  letterIndex,
} from "@/lib/tool-kit/ciphers/alphabet";
import {
  type EnigmaSettings,
  enigmaInPlace,
  type PlugboardProblem,
  parsePlugboard,
  REFLECTOR_NAMES,
  type ReflectorName,
  ROTOR_NAMES,
  type RotorName,
  rotorsDistinct,
  runEnigma,
  type Triple,
} from "@/lib/tool-kit/ciphers/enigma";

type OutputStyle = "groups" | "inPlace";
type PresetId = "check" | "double" | "message";

interface Preset {
  reflector: ReflectorName;
  rotors: Triple<RotorName>;
  rings: string;
  start: string;
  plugs: string;
  sample: "sampleCheck" | "sampleDouble" | "sampleMessage";
}

/** Settings for the examples on the page. The message is German Wikipedia's worked one. */
const PRESETS: Readonly<Record<PresetId, Preset>> = {
  check: {
    reflector: "B",
    rotors: ["I", "II", "III"],
    rings: "AAA",
    start: "AAA",
    plugs: "",
    sample: "sampleCheck",
  },
  double: {
    reflector: "B",
    rotors: ["I", "II", "III"],
    rings: "AAA",
    start: "ADU",
    plugs: "",
    sample: "sampleDouble",
  },
  message: {
    reflector: "B",
    rotors: ["I", "IV", "III"],
    rings: "PZH",
    start: "RTZ",
    plugs: "AD CN ET FL GI JV KZ PU QY WX",
    sample: "sampleMessage",
  },
};

/** English strings. A translated content file overrides these through `ui`. */
export const ENIGMA_MACHINE_STRINGS = {
  examples: "Load an example",
  exampleCheck: "AAAAA → BDZGO",
  exampleDouble: "The double step",
  exampleMessage: "A full message",
  sampleCheck: "AAAAA",
  sampleDouble: "AAAA",
  sampleMessage:
    "EJZLB SYEQP DWDUE EJJOU PSOFL BMUIM GLCSK BKJLY ZTEIY THZLU EUHRR KUZOW BVXFO UIZHY GVDXW QKKSB CPTVM NGUCL TQISS BTNSF GNFZC QSJAR CNOSE GWMYC HNODW FGGZC QNHZY FATHT QWKGU NWHOX BWKFN PYAMV FT",
  settingsHeading: "Machine settings",
  reflector: "Reflector",
  slotLeft: "Left",
  slotMiddle: "Middle",
  slotRight: "Right",
  rotorFor: "{slot} rotor",
  ringFor: "{slot} ring setting",
  startFor: "{slot} start position",
  ringOption: "{letter} · {number}",
  duplicateRotors:
    "Each rotor can sit in only one slot — the Enigma I came with one of each.",
  plugboard: "Plugboard pairs",
  plugboardPlaceholder: "AV BS CG",
  plugboardHint:
    "Up to ten pairs of letters separated by spaces, such as AV BS CG. A letter can be in one pair only.",
  plugOdd: "“{letter}” is not a pair of letters, so it was ignored.",
  plugSame: "{letter} cannot be plugged to itself, so that pair was ignored.",
  plugReused: "{letter} is already plugged, so that pair was ignored.",
  plugTooMany:
    "The Enigma I had ten cables: pair {count} and any after it were ignored.",
  message: "Message — typing it presses the keys",
  messagePlaceholder: "Type your message, or press the keys below",
  notTyped:
    "The machine had only the 26 letter keys, so spaces, digits and punctuation are not typed. Operators spelled numbers out and wrote X for a full stop.",
  keyboard: "Keyboard",
  pressKey: "Press {letter}",
  deleteLast: "Delete last letter",
  clearMessage: "Clear message",
  machineHeading: "The machine",
  windowsHeading: "Rotor windows",
  windowsHint:
    "After the last key press. Each press moves the right rotor on before the current flows, so the first letter is enciphered one position after the start.",
  windowLabel: "{slot} window shows {letter}",
  lampboard: "Lamp board",
  lampLit: "Lamp {letter} is lit",
  lampsDark: "No lamp is lit",
  pathHeading: "Path of the last key press",
  pathHint:
    "Through the plugboard, the rotors right to left, the reflector, and back through the rotors and the plugboard to a lamp.",
  stageKey: "Key",
  stagePlugboard: "Plugboard",
  stageRotor: "{rotor} ({slot})",
  stageReflector: "Reflector {name}",
  stageLamp: "Lamp",
  output: "Output",
  outputStyle: "Write the output as",
  outputGroups: "Five-letter groups",
  outputInPlace: "Letters in place of the message's letters",
  outputEmpty: "The lamps that light appear here.",
  letterCount: "one: {count} letter typed|other: {count} letters typed",
  copy: "Copy output",
  copied: "Copied!",
  decodeNote:
    "To decode, set the same rotors, rings, plugboard and start position and type the cipher text — the Enigma is its own inverse.",
  logHeading: "Key presses",
  logIndex: "#",
  logWindows: "Windows",
  logKey: "Key",
  logLamp: "Lamp",
  logLimit:
    "one: Showing the last key press.|other: Showing the last {count} key presses.",
} as const;

export type EnigmaStrings = typeof ENIGMA_MACHINE_STRINGS;

const LETTER_OPTIONS = [...ALPHABET].map((letter) => ({
  value: letter,
  label: letter,
}));

function toTriple(letters: string): Triple<number> {
  const [a, b, c] = [...letters].map((letter) => letterIndex(letter));
  return [a, b, c];
}

function plugProblem(
  problem: PlugboardProblem,
  strings: EnigmaStrings
): string {
  switch (problem.kind) {
    case "odd":
      return t(strings.plugOdd, { letter: problem.letter });
    case "same":
      return t(strings.plugSame, { letter: problem.letter });
    case "reused":
      return t(strings.plugReused, { letter: problem.letter });
    default:
      return t(strings.plugTooMany, { count: problem.count });
  }
}

/** Rotor, ring and start position for one slot. */
function SlotSettings({
  index,
  rotor,
  ring,
  start,
  onRotor,
  onRing,
  onStart,
  strings,
}: {
  index: number;
  rotor: RotorName;
  ring: string;
  start: string;
  onRotor: (value: RotorName) => void;
  onRing: (value: string) => void;
  onStart: (value: string) => void;
  strings: EnigmaStrings;
}) {
  const fmt = useToolNumber();
  const slot = slotNames(strings)[index];
  const ringOptions = [...ALPHABET].map((letter, position) => ({
    value: letter,
    label: t(strings.ringOption, {
      letter,
      number: fmt(position + 1, { minimumIntegerDigits: 2 }),
    }),
  }));
  return (
    <div className="space-y-3 rounded-lg border border-border p-3">
      <SelectField
        id={`en-rotor-${index}`}
        label={t(strings.rotorFor, { slot })}
        onChange={onRotor}
        options={ROTOR_NAMES.map((name) => ({ value: name, label: name }))}
        value={rotor}
      />
      <SelectField
        id={`en-ring-${index}`}
        label={t(strings.ringFor, { slot })}
        onChange={onRing}
        options={ringOptions}
        value={ring}
      />
      <SelectField
        id={`en-start-${index}`}
        label={t(strings.startFor, { slot })}
        onChange={onStart}
        options={LETTER_OPTIONS}
        value={start}
      />
    </div>
  );
}

function replaceAt(letters: string, index: number, letter: string): string {
  return letters.slice(0, index) + letter + letters.slice(index + 1);
}

export default function EnigmaMachineWidget() {
  const strings = useToolUi(ENIGMA_MACHINE_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [reflector, setReflector] = useState<ReflectorName>("B");
  const [rotors, setRotors] = useState<Triple<RotorName>>(["I", "II", "III"]);
  const [rings, setRings] = useState("AAA");
  const [start, setStart] = useState("AAA");
  const [plugText, setPlugText] = useState("");
  const [message, setMessage] = useState<string>(strings.sampleCheck);
  const [style, setStyle] = useState<OutputStyle>("groups");

  function loadPreset(id: PresetId) {
    const preset = PRESETS[id];
    setReflector(preset.reflector);
    setRotors(preset.rotors);
    setRings(preset.rings);
    setStart(preset.start);
    setPlugText(preset.plugs);
    setMessage(strings[preset.sample]);
  }

  function setRotor(index: number, rotor: RotorName) {
    const next = [...rotors] as [RotorName, RotorName, RotorName];
    next[index] = rotor;
    setRotors(next);
  }

  const plugboard = parsePlugboard(plugText);
  const distinct = rotorsDistinct(rotors);
  const settings: EnigmaSettings = {
    reflector,
    rotors,
    rings: toTriple(rings),
    positions: toTriple(start),
    plugs: plugboard.pairs,
  };
  const run = distinct ? runEnigma(message, settings) : null;
  const typed = run?.presses.length ?? 0;
  let output = "";
  if (run) {
    output =
      style === "groups"
        ? groupLetters(run.letters, 5)
        : enigmaInPlace(message, run);
  }

  return (
    <>
      <section className="space-y-2">
        <p className={LABEL}>{strings.examples}</p>
        <div className="flex flex-wrap gap-2">
          {(
            [
              ["check", strings.exampleCheck],
              ["double", strings.exampleDouble],
              ["message", strings.exampleMessage],
            ] as const
          ).map(([id, label]) => (
            <button
              className={BUTTON_SMALL}
              key={id}
              onClick={() => loadPreset(id)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
      </section>

      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.settingsHeading}</h2>
        <PillGroup
          label={strings.reflector}
          name="en-reflector"
          onChange={setReflector}
          options={REFLECTOR_NAMES.map((name) => ({
            value: name,
            label: name,
          }))}
          value={reflector}
        />
        <div className="grid gap-3 sm:grid-cols-3">
          {rotors.map((rotor, index) => (
            <SlotSettings
              index={index}
              // biome-ignore lint/suspicious/noArrayIndexKey: the three slots are fixed positions
              key={index}
              onRing={(letter) => setRings(replaceAt(rings, index, letter))}
              onRotor={(name) => setRotor(index, name)}
              onStart={(letter) => setStart(replaceAt(start, index, letter))}
              ring={rings.charAt(index)}
              rotor={rotor}
              start={start.charAt(index)}
              strings={strings}
            />
          ))}
        </div>
        {distinct ? null : <ErrorNotice>{strings.duplicateRotors}</ErrorNotice>}
        <TextField
          hint={strings.plugboardHint}
          id="en-plugs"
          label={strings.plugboard}
          mono
          onChange={setPlugText}
          placeholder={strings.plugboardPlaceholder}
          value={plugText}
        />
        {plugboard.problems.map((problem, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: the same problem can repeat, in order
          <ErrorNotice key={index}>{plugProblem(problem, strings)}</ErrorNotice>
        ))}
      </section>

      <section className="space-y-3">
        <TextAreaField
          id="en-message"
          label={strings.message}
          onChange={setMessage}
          placeholder={strings.messagePlaceholder}
          value={message}
        />
        <p className={HINT}>{strings.notTyped}</p>
      </section>

      <section className={`${CARD} space-y-5`}>
        <h2 className={WIDGET_H2}>{strings.machineHeading}</h2>
        <div className="grid gap-5 md:grid-cols-[auto_1fr] md:items-start">
          <RotorWindows
            rotors={rotors}
            strings={strings}
            window={run?.finalWindow ?? start}
          />
          <Lampboard
            lit={run?.presses.at(-1)?.output ?? null}
            strings={strings}
          />
        </div>
        <Keyboard
          canDelete={message !== ""}
          onClear={() => setMessage("")}
          onDelete={() => setMessage(message.slice(0, -1))}
          onPress={(letter) => setMessage(message + letter)}
          strings={strings}
        />
        {run?.lastPath ? (
          <SignalRoute
            path={run.lastPath}
            reflector={reflector}
            rotors={rotors}
            strings={strings}
          />
        ) : null}
      </section>

      <section aria-live="polite" className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.output}</h2>
        <PillGroup
          label={strings.outputStyle}
          name="en-output-style"
          onChange={setStyle}
          options={[
            { value: "groups", label: strings.outputGroups },
            { value: "inPlace", label: strings.outputInPlace },
          ]}
          value={style}
        />
        {typed === 0 ? (
          <Notice>{strings.outputEmpty}</Notice>
        ) : (
          <p className="whitespace-pre-wrap break-all rounded-lg border border-border bg-card p-4 font-mono text-foreground text-lg tracking-wide">
            {output}
          </p>
        )}
        <p className={HINT}>
          {tPlural(strings.letterCount, locale, typed, { count: fmt(typed) })}
        </p>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={output}
        />
        <p className={HINT}>{strings.decodeNote}</p>
      </section>

      {run && typed > 0 ? (
        <section className={CARD}>
          <PressLog presses={run.presses} strings={strings} />
        </section>
      ) : null}
    </>
  );
}
